import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { BellRing, CircleAlert, Clock, CreditCard, Eye, Inbox, LayoutDashboard, Loader2, MailCheck, Megaphone, RefreshCw, Send, ShieldCheck, Sparkles, UserPlus, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { EMAIL_KIND_LABEL, EMAIL_TEST_STATUS, getOwnerStats, listRecentEmails, listUpdates, previewUpdate, sendUpdate, testUpdate } from '../lib/adminApi';
import { apiErrorMessage } from '../lib/projectApi';
import { SiteHeader } from '../components/billing/SiteHeader';
import { SiteFooter } from '../components/legal/SiteFooter';
import { EmailTestDialog } from '../components/editor/EmailTestDialog';
import { OwnerNav } from '../components/owner/OwnerNav';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

const TONE_BADGE = {
  wait: 'bg-primary/15 text-primary hover:bg-primary/15',
  ok: 'bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))] hover:bg-[hsl(var(--wp-context-green))]/15',
  warn: 'bg-[hsl(var(--wp-context-amber))]/20 text-[hsl(var(--wp-context-amber))] hover:bg-[hsl(var(--wp-context-amber))]/20',
  bad: 'bg-destructive/15 text-destructive hover:bg-destructive/15',
  muted: 'bg-muted text-muted-foreground hover:bg-muted',
};

const statusBadge = (status) => EMAIL_TEST_STATUS[status] || { label: status || '—', tone: 'muted' };

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)} h ago`;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' }).slice(0, 2);
};

/* ------------------------------------------------------------------ */
/* Stat cards + the 14-day sign-up chart                                */
/* ------------------------------------------------------------------ */

const StatCard = ({ icon: Icon, label, value, detail, testId, loading }) => (
  <div className="rounded-xl border border-border bg-card p-4 min-w-0" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-medium text-muted-foreground truncate">{label}</span>
      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-3.5 w-3.5" aria-hidden="true" /></span>
    </div>
    {loading ? <Skeleton className="mt-2 h-8 w-20" /> : <div className="mt-1.5 text-2xl font-semibold tracking-tight tabular-nums" style={heading} data-testid={`${testId}-value`}>{value}</div>}
    {detail && !loading && <div className="mt-1 text-[11px] text-muted-foreground leading-snug" data-testid={`${testId}-detail`}>{detail}</div>}
  </div>
);

const SignupsChart = ({ days }) => {
  const max = Math.max(1, ...days.map((d) => d.count));
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="owner-signups-chart">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold" style={heading}>Sign-ups · last 14 days</div>
        <div className="text-[11px] text-muted-foreground">{days.reduce((n, d) => n + d.count, 0)} total</div>
      </div>
      <ol className="mt-3 grid grid-cols-[repeat(14,minmax(0,1fr))] gap-1 items-end h-28" aria-label="Sign-ups per day">
        {days.map((d) => {
          const h = d.count ? Math.max(8, Math.round((d.count / max) * 100)) : 3;
          const isToday = d.day === today;
          return (
            <li key={d.day} className="flex flex-col items-center justify-end h-full min-w-0" title={`${d.day}: ${d.count} sign-up${d.count === 1 ? '' : 's'}`} data-testid="owner-signups-bar">
              {d.count > 0 && <span className="text-[10px] tabular-nums text-muted-foreground mb-0.5">{d.count}</span>}
              <span className={`w-full rounded-t-sm ${d.count ? (isToday ? 'bg-primary' : 'bg-primary/60') : 'bg-border'}`} style={{ height: `${h}%` }} aria-hidden="true" />
              <span className={`mt-1 text-[10px] ${isToday ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{dayLabel(d.day)}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Recent deliveries                                                     */
/* ------------------------------------------------------------------ */

const Deliveries = ({ rows, loading, error, onRefresh, refreshing }) => (
  <section className="rounded-xl border border-border bg-card" data-testid="owner-deliveries">
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><MailCheck className="h-4 w-4" aria-hidden="true" /></span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-tight" style={heading}>Recent email deliveries</h2>
          <p className="text-[11px] text-muted-foreground">Every email the app sent, with the provider's latest delivery event.</p>
        </div>
      </div>
      <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={onRefresh} disabled={refreshing} data-testid="owner-deliveries-refresh"><RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh</Button>
    </div>
    {error && <p className="px-4 py-3 text-xs text-destructive flex items-center gap-1.5" role="alert" data-testid="owner-deliveries-error"><CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />{error}</p>}
    {loading ? (
      <div className="p-4 space-y-2" aria-busy="true"><Skeleton className="h-7 w-full" /><Skeleton className="h-7 w-11/12" /><Skeleton className="h-7 w-10/12" /></div>
    ) : rows.length === 0 ? (
      <div className="p-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="owner-deliveries-empty"><Inbox className="h-6 w-6 text-primary" aria-hidden="true" />Nothing sent yet. Welcome, reset and billing emails will show up here as they go out.</div>
    ) : (
      <Table className="text-xs table-fixed w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-9 w-[84px] sm:w-[96px] whitespace-nowrap">When</TableHead>
            <TableHead className="h-9 w-[110px] sm:w-[150px]">Type</TableHead>
            <TableHead className="h-9">To</TableHead>
            <TableHead className="h-9 hidden md:table-cell">Subject</TableHead>
            <TableHead className="h-9 w-[96px] sm:w-[120px] text-right">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const b = statusBadge(r.status);
            return (
              <TableRow key={r.id} data-testid="owner-delivery-row">
                <TableCell className="py-2 truncate text-muted-foreground" title={new Date(r.sent_at).toLocaleString()}>{when(r.sent_at)}</TableCell>
                <TableCell className="py-2 truncate" title={EMAIL_KIND_LABEL[r.kind] || r.kind}>{EMAIL_KIND_LABEL[r.kind] || r.kind}</TableCell>
                <TableCell className="py-2 truncate" title={r.to}>{r.to}</TableCell>
                <TableCell className="py-2 hidden md:table-cell truncate text-muted-foreground" title={r.subject}>{r.subject}</TableCell>
                <TableCell className="py-2 text-right"><Badge className={`${TONE_BADGE[b.tone]} whitespace-nowrap`} title={r.error || b.detail || ''} data-testid="owner-delivery-status">{b.label}</Badge></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    )}
  </section>
);

/* ------------------------------------------------------------------ */
/* Product update composer + past sends                                  */
/* ------------------------------------------------------------------ */

const Composer = ({ optIns, transport, onSent }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaPath, setCtaPath] = useState('');
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null); // { html, recipients }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const draft = useMemo(() => ({ subject, body, cta_label: ctaLabel || null, cta_path: ctaPath || null }), [subject, body, ctaLabel, ctaPath]);
  const ready = subject.trim().length > 0 && body.trim().length > 0 && !busy;
  const canSend = ready && optIns > 0 && transport === 'resend';

  const run = async (what, fn) => {
    if (busy) return;
    setBusy(what);
    setError('');
    try { await fn(); } catch (e) { setError(await apiErrorMessage(e, 'That did not work.')); } finally { setBusy(''); }
  };
  const doPreview = () => run('preview', async () => setPreview(await previewUpdate(draft)));
  const doTest = () => run('test', async () => { const r = await testUpdate(draft); toast.success('Test sent to you', { description: `Check ${r.to}.`, id: 'update' }); onSent?.(); });
  const doSend = () => run('send', async () => {
    const c = await sendUpdate(draft);
    setConfirmOpen(false);
    toast.success('Update is on its way', { description: `Sending to ${c.recipients} reader${c.recipients === 1 ? '' : 's'} — progress shows below.`, id: 'update' });
    setSubject(''); setBody(''); setCtaLabel(''); setCtaPath('');
    onSent?.(true);
  });

  return (
    <section className="rounded-xl border border-border bg-card" data-testid="owner-composer">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Megaphone className="h-4 w-4" aria-hidden="true" /></span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-tight" style={heading}>Send a product update</h2>
          <p className="text-[11px] text-muted-foreground">Goes only to people who opted in to tips &amp; product updates — <span className="text-foreground font-medium" data-testid="owner-composer-optins">{optIns}</span> right now. Every copy carries its own unsubscribe link.</p>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="update-subject" className="text-xs">Subject</Label>
          <Input id="update-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} placeholder="New in WannaPublish: …" className="h-10 bg-background" data-testid="owner-update-subject" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="update-body" className="text-xs">Message <span className="text-muted-foreground font-normal">— plain text; a blank line starts a new paragraph. “Hi &lt;first name&gt;,” is added for you.</span></Label>
          <Textarea id="update-body" value={body} onChange={(e) => setBody(e.target.value)} maxLength={6000} rows={7} placeholder={'Here is what is new this month…\n\nSecond paragraph…'} className="bg-background text-sm leading-relaxed" data-testid="owner-update-body" />
          <div className="text-[11px] text-muted-foreground text-right tabular-nums">{body.length} / 6000</div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="update-cta-label" className="text-xs">Button label <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="update-cta-label" value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} maxLength={40} placeholder="Open the Help Center" className="h-10 bg-background" data-testid="owner-update-cta-label" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="update-cta-path" className="text-xs">Button goes to <span className="text-muted-foreground font-normal">(a page on this site)</span></Label>
            <Input id="update-cta-path" value={ctaPath} onChange={(e) => setCtaPath(e.target.value)} placeholder="/help" className="h-10 bg-background font-mono text-xs" data-testid="owner-update-cta-path" />
          </div>
        </div>
        {error && <p className="text-xs text-destructive flex items-start gap-1.5" role="alert" data-testid="owner-update-error"><CircleAlert className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden="true" />{error}</p>}
        {transport !== 'resend' && <p className="text-xs text-[hsl(var(--wp-context-amber))] flex items-start gap-1.5" data-testid="owner-update-transport-warning"><CircleAlert className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden="true" />Product updates can only be sent from your own verified sending domain (Resend). The server is currently on the fallback transport.</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" variant="outline" className="h-10" onClick={doPreview} disabled={!ready} data-testid="owner-update-preview">{busy === 'preview' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <Eye className="h-4 w-4 mr-1.5" aria-hidden="true" />}Preview</Button>
          <Button type="button" variant="outline" className="h-10" onClick={doTest} disabled={!ready} data-testid="owner-update-test">{busy === 'test' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <MailCheck className="h-4 w-4 mr-1.5" aria-hidden="true" />}Send me a test</Button>
          <Button type="button" className="h-10" onClick={() => setConfirmOpen(true)} disabled={!canSend} title={optIns === 0 ? 'Nobody has opted in yet' : undefined} data-testid="owner-update-send">{busy === 'send' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4 mr-1.5" aria-hidden="true" />}Send to {optIns} reader{optIns === 1 ? '' : 's'}</Button>
        </div>
        <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>Only opted-in accounts receive it; the button can only link to a page on this site; sends go out one at a time and anyone who unsubscribes mid-run is skipped.</span></p>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-[640px] grid-cols-[minmax(0,1fr)] max-h-[90vh] overflow-y-auto bg-card" data-testid="owner-update-preview-dialog">
          <DialogHeader>
            <DialogTitle style={heading}>Preview</DialogTitle>
            <DialogDescription>How it looks in the inbox — addressed to you, with your unsubscribe link. Would go to {preview?.recipients ?? 0} reader{preview?.recipients === 1 ? '' : 's'}.</DialogDescription>
          </DialogHeader>
          {preview && <iframe title="Email preview" sandbox="" srcDoc={preview.html} className="w-full h-[520px] rounded-md border border-border bg-white" data-testid="owner-update-preview-frame" />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-card" data-testid="owner-update-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle style={heading}>Send “{subject.trim() || 'this update'}” to {optIns} reader{optIns === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be recalled once it starts. Send yourself a test first if you have not already.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-update-confirm-cancel">Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doSend(); }} disabled={busy === 'send'} data-testid="owner-update-confirm-send">{busy === 'send' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4 mr-1.5" aria-hidden="true" />}Send now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

const CAMPAIGN_BADGE = { sending: { label: 'Sending…', tone: 'wait' }, done: { label: 'Sent', tone: 'ok' } };

const Campaigns = ({ rows, loading }) => (
  <section className="rounded-xl border border-border bg-card" data-testid="owner-campaigns">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Sparkles className="h-4 w-4" aria-hidden="true" /></span>
      <h2 className="text-base font-semibold leading-tight" style={heading}>Past updates</h2>
    </div>
    {loading ? (
      <div className="p-4 space-y-2" aria-busy="true"><Skeleton className="h-7 w-full" /><Skeleton className="h-7 w-10/12" /></div>
    ) : rows.length === 0 ? (
      <p className="p-5 text-sm text-muted-foreground text-center" data-testid="owner-campaigns-empty">No product updates sent yet.</p>
    ) : (
      <ul className="divide-y divide-border">
        {rows.map((c) => {
          const b = CAMPAIGN_BADGE[c.status] || { label: c.status, tone: 'muted' };
          const pct = c.recipients ? Math.round(((c.sent + c.skipped + c.failed) / c.recipients) * 100) : 100;
          return (
            <li key={c.id} className="px-4 py-3 text-xs" data-testid="owner-campaign-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate" title={c.subject}>{c.subject}</div>
                  <div className="mt-0.5 text-muted-foreground">{when(c.created_at)} · {c.recipients} reader{c.recipients === 1 ? '' : 's'} · <span className="text-foreground">{c.sent} sent</span>{c.skipped ? ` · ${c.skipped} skipped` : ''}{c.failed ? <span className="text-destructive"> · {c.failed} failed</span> : ''}</div>
                </div>
                <Badge className={`${TONE_BADGE[b.tone]} shrink-0`}>{b.label}</Badge>
              </div>
              {c.status === 'sending' && <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden" aria-hidden="true"><div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} /></div>}
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

/* ------------------------------------------------------------------ */
/* The page                                                              */
/* ------------------------------------------------------------------ */

/**
 * /owner — the owner dashboard (accounts in OWNER_EMAILS): sign-ups (today / 7 d / 30 d + 14-day chart), trials and
 * subscribers, opt-ins, every email the app sent with its live delivery status, the "tips & product updates" composer
 * (preview → send me a test → send to everyone opted in) and the delivery-test tool. Numbers refresh every 30 s.
 */
export default function Owner() {
  const { user } = useAuth();
  const [theme, toggleTheme] = useSiteTheme();
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState('');
  const [emails, setEmails] = useState(null);
  const [emailsError, setEmailsError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [campaigns, setCampaigns] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const alive = useRef(true);

  useEffect(() => { document.title = 'Owner dashboard · WannaPublish'; alive.current = true; return () => { alive.current = false; }; }, []);

  const loadStats = useCallback(async () => {
    try { const s = await getOwnerStats(); if (alive.current) { setStats(s); setStatsError(''); } } catch (e) { if (alive.current) setStatsError(await apiErrorMessage(e, 'The numbers could not be loaded.')); }
  }, []);
  const loadEmails = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try { const rows = await listRecentEmails(25); if (alive.current) { setEmails(rows); setEmailsError(''); } } catch (e) { if (alive.current) setEmailsError(await apiErrorMessage(e, 'Recent deliveries could not be loaded.')); } finally { if (alive.current) setRefreshing(false); }
  }, []);
  const loadCampaigns = useCallback(async () => {
    try { const rows = await listUpdates(); if (alive.current) setCampaigns(rows); } catch { if (alive.current) setCampaigns((c) => c || []); }
  }, []);

  useEffect(() => { loadStats(); loadEmails(); loadCampaigns(); }, [loadStats, loadEmails, loadCampaigns]);
  useEffect(() => {
    const t = setInterval(() => { loadStats(); loadEmails(); }, 30000);
    return () => clearInterval(t);
  }, [loadStats, loadEmails]);
  // while an update is sending, follow its progress
  const sending = (campaigns || []).some((c) => c.status === 'sending');
  useEffect(() => {
    if (!sending) return undefined;
    const t = setInterval(() => { loadCampaigns(); loadEmails(); loadStats(); }, 3000);
    return () => clearInterval(t);
  }, [sending, loadCampaigns, loadEmails, loadStats]);

  const subs = stats?.subscriptions;
  const optIns = stats?.tips_opt_in ?? 0;

  return (
    <main className="min-h-[100dvh] flex flex-col bg-background text-foreground" data-testid="owner-page" data-theme={theme}>
      <SiteHeader theme={theme} onToggleTheme={toggleTheme} testId="owner-header" />
      <section className="flex-1 w-full max-w-[1180px] mx-auto px-4 sm:px-6 pb-12 pt-2 sm:pt-4">
        <OwnerNav />
        <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><LayoutDashboard className="h-5 w-5" aria-hidden="true" /></span>
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={heading} data-testid="owner-title">Owner dashboard</h1>
              <p className="mt-1 text-sm text-muted-foreground">How WannaPublish is doing — accounts, subscriptions and email, for <span className="text-foreground font-medium">{user?.email}</span>.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="h-10" onClick={() => setTestOpen(true)} data-testid="owner-open-email-test"><MailCheck className="h-4 w-4 mr-1.5" aria-hidden="true" />Send me a test email</Button>
            <Button type="button" variant="outline" className="h-10" onClick={() => { loadStats(); loadEmails(true); loadCampaigns(); }} disabled={refreshing} data-testid="owner-refresh-all"><RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh</Button>
          </div>
        </div>

        {statsError && <p className="mt-4 text-xs text-destructive flex items-center gap-1.5" role="alert" data-testid="owner-stats-error"><CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />{statsError}</p>}

        <div className="mt-6 grid gap-3 grid-cols-2 lg:grid-cols-5" data-testid="owner-stat-cards">
          <StatCard icon={UserPlus} label="Sign-ups" value={stats?.signups.total ?? 0} detail={stats ? `${stats.signups.today} today · ${stats.signups.last_7d} in 7 days · ${stats.signups.last_30d} in 30 days` : ''} loading={!stats} testId="owner-stat-signups" />
          <StatCard icon={Clock} label="Active trials" value={subs?.trialing ?? 0} detail={subs ? (subs.canceling ? `${subs.canceling} set to cancel` : 'Card on file, not charged yet') : ''} loading={!stats} testId="owner-stat-trials" />
          <StatCard icon={CreditCard} label="Subscribers" value={subs ? subs.active + subs.past_due : 0} detail={subs ? `${subs.active} paying${subs.past_due ? ` · ${subs.past_due} past due` : ''}${subs.ended ? ` · ${subs.ended} ended` : ''}` : ''} loading={!stats} testId="owner-stat-subscribers" />
          <StatCard icon={BellRing} label="Tips opt-ins" value={optIns} detail={stats ? `${stats.signups.total ? Math.round((optIns / stats.signups.total) * 100) : 0}% of accounts` : ''} loading={!stats} testId="owner-stat-optins" />
          <StatCard icon={Users} label="Emails · 7 days" value={stats?.emails_7d.sent ?? 0} detail={stats ? `${stats.emails_7d.delivered} delivered${stats.emails_7d.bounced ? ` · ${stats.emails_7d.bounced} bounced` : ''}${stats.emails_7d.failed ? ` · ${stats.emails_7d.failed} failed` : ''}` : ''} loading={!stats} testId="owner-stat-emails" />
        </div>

        <div className="mt-3">{stats ? <SignupsChart days={stats.signups.by_day} /> : <Skeleton className="h-44 w-full rounded-xl" />}</div>

        <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
          <div className="space-y-4 min-w-0">
            <Deliveries rows={emails || []} loading={emails === null} error={emailsError} onRefresh={() => loadEmails(true)} refreshing={refreshing} />
          </div>
          <div className="space-y-4 min-w-0">
            <Composer optIns={optIns} transport={stats?.email_transport || 'resend'} onSent={(campaign) => { loadEmails(); if (campaign) loadCampaigns(); }} />
            <Campaigns rows={campaigns || []} loading={campaigns === null} />
          </div>
        </div>
      </section>
      <SiteFooter testId="owner-footer" />
      <EmailTestDialog open={testOpen} onOpenChange={setTestOpen} />
    </main>
  );
}
